import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { workspaceMessages } from "./workspace";

/**
 * THE ONE DOOR into the workspace settings' dictionary.
 *
 * One scope for eleven cards, and one dictionary behind it. `PreferencesCard`
 * used to mount `workspaceMessages` inline and it was the only card that spoke;
 * the rest each held their own sentences, which is how the same word ends up
 * spelled two ways on one screen.
 *
 * `lastSignal` rides along because the team roster's "há 5 min" is a sentence,
 * not a date: `lastSeenAt` means activity and not last sign-in, so nothing in
 * that column is more precise than words.
 *
 * ── WHY THE SCOPE IS AN ARGUMENT ──────────────────────────────────────────
 *
 * A component holds exactly ONE local scope. `useI18n({ useScope: "local" })`
 * caches its composer on the component instance, so a second call hands back the
 * FIRST one and drops the second bag of messages on the floor (vue-i18n says
 * `DUPLICATE_USE_I18N_CALLING` and carries on) — the words then resolve against
 * the wrong dictionary and the screen draws raw key names.
 *
 * Eleven cards call this and nothing else, so `local` is right for them and stays
 * the default. `pages/TeamMemberRow.vue` is the exception: it already mounts the
 * settings pages' dictionary and needs the roster's words on top, so it asks for
 * `isolated` — an independent composer, not tied to the instance, that still
 * inherits the panel's language from the root.
 */
export function useWorkspaceI18n(scope: "local" | "isolated" = "local") {
	const { t, te, locale } = useI18n({
		useScope: scope,
		inheritLocale: true,
		messages: workspaceMessages,
	});

	/**
	 * How long since someone was last seen, in words.
	 *
	 * Words and not a timestamp on purpose: `lastSeenAt` is written by the token
	 * guard on every authenticated request, dammed at two minutes, so showing
	 * seconds would assert a precision the data does not have.
	 */
	function lastSignal(iso: string | null): string {
		if (!iso) return t("teams.neverSignedIn");
		const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
		if (minutes < 3) return t("teams.now");
		if (minutes < 60) return t("teams.minutesAgo", { n: minutes });
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return t("teams.hoursAgo", { n: hours });
		return t("teams.daysAgo", { n: Math.floor(hours / 24) });
	}

	/** Why a row cannot be unticked: it is you, or it is the last owner standing. */
	function lockedReason(isSelf: boolean, isSoleOwner: boolean): string | null {
		if (isSelf) return t("teams.itIsYou");
		if (isSoleOwner) return t("teams.soleOwner");
		return null;
	}

	/**
	 * A role's name, keyed by the SERVER's code.
	 *
	 * Read from `role.*` in the ROOT composer (`shared/i18n/common.ts`) and not
	 * from this feature's dictionary: the same three words appear on the roster,
	 * on your own account card and in an invitation, and a copy per screen is how
	 * one of them ends up saying "Dono" while the next says "Proprietário". A
	 * local scope falls through to the root on a miss, so nothing is imported.
	 *
	 * A role this map has not met shows its own code — readable, and better than
	 * a blank badge beside somebody's name.
	 */
	const roleLabel = (role: string): string => (te(`role.${role}`) ? t(`role.${role}`) : role);

	/** The same three, in the shape `SelectField` takes. */
	const roleOptions = computed(() =>
		(["owner", "admin", "member"] as const).map((value) => ({ value, label: roleLabel(value) })),
	);

	return { t, te, locale, lastSignal, lockedReason, roleLabel, roleOptions };
}
